import { ChangeEvent, ElementType, FormEvent, RefObject } from 'react'

import { generateRandomID } from '@/utils/idGenerator'

import { Tokens } from '@/assets/tokens'
import { InnerContainer, InputContainer, InputIn, Label } from './Input.styles'

interface Props {
  id?: string
  value?: string
  type?: string
  min?: number
  max?: number
  innerRef?: RefObject<HTMLInputElement>
  preMask?: string
  label?: string
  icon?: ElementType
  placeholder?: string
  required?: boolean
  minLength?: number
  maxLength?: number
  date?: boolean
  disabled?: boolean
  readOnly?: boolean
  innerBackground?: string
  onChange: (value: string) => void
  onKeyDown?: (key: string) => void
}

export function Input({
  id,
  value,
  innerRef,
  preMask,
  label,
  icon: Icon,
  placeholder,
  required,
  minLength,
  maxLength,
  date,
  disabled,
  readOnly,
  innerBackground = Tokens.ColorBackground,
  onChange,
  onKeyDown,
  ...rest
}: Props) {
  const inputID = id || generateRandomID()

  function onInput(input: FormEvent<HTMLInputElement>) {
    if (maxLength && input.currentTarget.value.length > maxLength) {
      input.currentTarget.value = input.currentTarget.value.slice(0, maxLength)
    }
    if (date) {
      const inputValue = input.currentTarget.value
      const cleanedValue = inputValue.replace(/[^\d/]/g, '')
      const formattedValue = cleanedValue.slice(0, 10)
      input.currentTarget.value = formattedValue
        .replace(/^(\d{2})(\d{2})(\d{4})$/, '$1/$2/$3')
        .replace(/^(\d{2})\/(\d{2})\/(\d{4})\d+$/, '$1/$2/$3')
    }
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!onChange) return
    onChange(event.target.value)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (!onKeyDown) return
    onKeyDown(event.key)
  }

  return (
    <InputContainer className="input-container">
      {label && <Label htmlFor={inputID}>{`${label}${required ? '*' : ''}`}</Label>}
      <InnerContainer style={{ backgroundColor: innerBackground }}>
        {Icon && <Icon />}
        {preMask && <span>{preMask}</span>}
        <InputIn
          ref={innerRef}
          id={inputID}
          value={value}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          onInput={onInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          readOnly={readOnly}
          {...rest}
        />
      </InnerContainer>
    </InputContainer>
  )
}
